import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { UserPlus as UserPlusIcon, X as CloseIcon } from "lucide-react";
import type { ContactGroupSummary, CreateContactRequest } from "@multi-chat/shared";
import { Button } from "../../../components/ui/button/index.js";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "../../../components/ui/sheet/index.js";
import { cn } from "../../../lib/utils.js";
import { ContactGroupMultiSelect } from "../ContactGroupMultiSelect/index.js";
import { ContactBirthdayField } from "../ContactBirthdayField.js";
import { ContactFormField, ContactFormSection } from "../ContactFormField.js";
import { InternationalPhoneField } from "../../phone/InternationalPhoneField.js";
import {
  contactLimitFeedback,
  isContactLimitReachedError,
  mapContactError,
} from "../contact-errors.js";
import { useOptionalNotifications } from "../../notifications/notification-context.js";
import {
  contactFormTwoColClass,
  contactSheetBodyClass,
  contactSheetDescriptionClass,
  contactSheetFooterClass,
  contactSheetHeaderClass,
  contactSheetHeaderIconClass,
  contactSheetTitleClass,
  CONTACT_CRM_SHEET_CLASS,
} from "../contact-sheet-presentation.js";
import {
  CONTACT_SHEET_MOTION_CLASS,
  CONTACT_SHEET_OVERLAY_MOTION_CLASS,
} from "../contacts-table-presentation.js";
import {
  contactGroupSelectionToRequest,
  emptyContactGroupSelection,
  type ContactGroupSelectionValue,
} from "../contact-group-selection-value.js";

export function ContactFormSheet({
  open,
  groups,
  defaults,
  onClose,
  onSubmit,
}: {
  open: boolean;
  groups: ContactGroupSummary[];
  defaults?: {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    email?: string;
    phone?: string;
    phoneCountryIso?: string;
  };
  onClose: () => void;
  onSubmit: (body: CreateContactRequest) => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const notifications = useOptionalNotifications();
  const titleId = useId();
  const descriptionId = useId();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountryIso, setPhoneCountryIso] = useState("IL");
  const [birthday, setBirthday] = useState("");
  const [groupSelection, setGroupSelection] = useState<ContactGroupSelectionValue>(
    emptyContactGroupSelection(),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  useEffect(() => {
    if (!open) {
      setError(null);
      setSaving(false);
      setGroupSelection(emptyContactGroupSelection());
      return;
    }
    const next = defaultsRef.current;
    setFirstName(next?.firstName ?? "");
    setLastName(next?.lastName ?? "");
    setDisplayName(next?.displayName ?? "");
    setEmail(next?.email ?? "");
    setPhone(next?.phone ?? "");
    setPhoneCountryIso(next?.phoneCountryIso ?? "IL");
    setBirthday("");
    setGroupSelection(emptyContactGroupSelection());
    setError(null);
  }, [open]);

  const handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (saving) return;

    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        displayName: displayName || undefined,
        email: email || undefined,
        phone: phone || undefined,
        phoneCountryIso,
        birthday: birthday || undefined,
        ...contactGroupSelectionToRequest(groupSelection),
      });
    } catch (err) {
      if (isContactLimitReachedError(err)) {
        const copy = contactLimitFeedback(t, "create");
        notifications?.pushNotification({
          kind: "error",
          message: copy.title,
          detail: copy.message,
        });
      } else {
        setError(mapContactError(t, err, t("contacts.create.error")));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="end"
        className={cn(CONTACT_CRM_SHEET_CLASS, CONTACT_SHEET_MOTION_CLASS)}
        overlayClassName={CONTACT_SHEET_OVERLAY_MOTION_CLASS}
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <header className={contactSheetHeaderClass}>
          <div className="flex min-w-0 items-start gap-3">
            <span className={contactSheetHeaderIconClass} aria-hidden="true">
              <UserPlusIcon className="size-4" />
            </span>
            <div className="grid min-w-0 gap-1">
              <SheetTitle id={titleId} className={contactSheetTitleClass}>
                {t("contacts.create.title")}
              </SheetTitle>
              <SheetDescription id={descriptionId} className={contactSheetDescriptionClass}>
                {t("contacts.create.description")}
              </SheetDescription>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            className="size-8 shrink-0 p-0 text-muted hover:text-foreground"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <CloseIcon className="size-4" aria-hidden="true" />
          </Button>
        </header>

        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => void handleSubmit(event)}
          noValidate
        >
          <div className={cn(contactSheetBodyClass, "grid gap-4 content-start")}>
            <ContactFormSection legend={t("contacts.create.sections.basicDetails")}>
              <div className={contactFormTwoColClass}>
                <ContactFormField
                  id="contact-create-first-name"
                  label={t("contacts.columns.firstName")}
                  placeholder={t("contacts.columns.firstName")}
                  value={firstName}
                  disabled={saving}
                  onChange={setFirstName}
                />
                <ContactFormField
                  id="contact-create-last-name"
                  label={t("contacts.columns.lastName")}
                  placeholder={t("contacts.columns.lastName")}
                  value={lastName}
                  disabled={saving}
                  onChange={setLastName}
                />
                <div className="sm:col-span-2">
                  <ContactFormField
                    id="contact-create-display-name"
                    label={t("contacts.columns.displayName")}
                    placeholder={t("contacts.columns.displayName")}
                    value={displayName}
                    disabled={saving}
                    onChange={setDisplayName}
                  />
                </div>
                <div className="sm:col-span-2">
                  <ContactBirthdayField
                    id="contact-create-birthday"
                    value={birthday}
                    disabled={saving}
                    onChange={setBirthday}
                  />
                </div>
              </div>
            </ContactFormSection>

            <ContactFormSection legend={t("contacts.create.sections.communication")}>
              <div className="grid gap-2.5">
                <ContactFormField
                  id="contact-create-email"
                  label={t("contacts.columns.email")}
                  type="email"
                  placeholder={t("contacts.columns.email")}
                  value={email}
                  disabled={saving}
                  onChange={setEmail}
                />
                <div className="min-w-0" data-testid="contact-create-phone-field">
                  <InternationalPhoneField
                    id="contact-create-phone"
                    value={phone}
                    defaultCountryIso={phoneCountryIso}
                    onChange={setPhone}
                    onCountryIsoChange={setPhoneCountryIso}
                    disabled={saving}
                  />
                </div>
              </div>
            </ContactFormSection>

            <ContactFormSection legend={t("contacts.groups.multiSelect.label")}>
              <ContactGroupMultiSelect
                groups={groups}
                value={groupSelection}
                onChange={setGroupSelection}
                disabled={saving}
                presentation="section"
              />
            </ContactFormSection>
          </div>

          <footer className={contactSheetFooterClass}>
            <div className="grid gap-3">
              {error ? (
                <p className="m-0 rounded-lg border border-red-200/80 bg-red-50 px-3 py-2 text-[0.8125rem] text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
                  {error}
                </p>
              ) : null}
              <Button type="submit" className="h-9 w-full text-[0.8125rem] font-semibold" disabled={saving}>
                {saving ? t("contacts.create.submitting") : t("contacts.create.submit")}
              </Button>
            </div>
          </footer>
        </form>
      </SheetContent>
    </Sheet>
  );
}
